import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";

import { apiGetHfLabDataByPatientIdOptions } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useSupportedMedicalData } from "../../../catalogs/supported-medical-data";
import { AddButton } from "../../../components/ui-kit/add-button";
import { Select } from "../../../components/ui-kit/fields/select";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { MedicalPlotModel } from "../../../models/plotting";
import { getUnitsValuesByName } from "../../../utils/data-utils";
import { requireNonNull } from "../../../utils/utils";
import { MedData, MedDataItem } from "../common/med-data";
import { PatientSubpage } from "../common/patient-subpage";
import { PlotCanvas, PlotControls, usePlot } from "../common/plot";

export const LabsTable = observer(function LabsTable(props: { app: AppModel }) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const supportedMedData = useSupportedMedicalData();

  const userUUID = api.requireUserUUID();
  const model = useMemo(
    () => new MedData<MedDataItem>([], api.permissions, userUUID),
    [api.permissions, userUUID],
  );

  const { data, refetch } = useQuery(
    apiGetHfLabDataByPatientIdOptions({
      path: { patient_id: patient.patientId },
    }),
  );

  useEffect(() => {
    if (data) model.updateData(data.medical_data);
  }, [data, model]);

  const plot = usePlot(
    () =>
      new MedicalPlotModel(
        model,
        ["NT-proBNP", "BNP", "urea", "creatinine", "hemoglobin", "hematocrit"],
        "plot-canvas",
      ),
  );

  return (
    <PatientSubpage title={t`Labs`} loading={!model.initialized}>
      <PlotCanvas model={plot} />

      <DataTable
        filterActions={
          <>
            <AddButton href="add">
              <Trans>Add labs</Trans>
            </AddButton>
            <PlotControls model={plot} />
          </>
        }
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        columns={[
          {
            header: <Trans>Measured</Trans>,
            attr: "measurement_datetime",
            type: "datetime",
            editable: true,
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Type</Trans>,
            attr: "measurement_type",
            type: "string",
            sort: true,
          },
          {
            header: <Trans>Value</Trans>,
            attr: "measurement_value",
            type: "number",
            precision: 1,
            editable: true,
          },
          {
            header: <Trans>Unit</Trans>,
            attr: "measurement_unit",
            editable: true,
            editInDialog: true,
            cell: (p) => {
              if (!p.patch) {
                return <>{p.item.measurement_unit}</>;
              }
              // Mirror the add flow: pick among the variable's allowed units,
              // disabled when there's only the primary unit.
              const unitOptions = getUnitsValuesByName(
                supportedMedData,
                p.item.measurement_type,
              );
              return (
                <Select
                  model={p.patch}
                  attr={requireNonNull(p.attr)}
                  values={unitOptions}
                  disabled={unitOptions.length <= 1}
                />
              );
            },
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            multiline: true,
            editable: true,
          },
          model.showingHistory && {
            header: <Trans>Added</Trans>,
            attr: "created_at",
            type: "datetime",
          },
          model.showingHistory && {
            header: <Trans>Last edit</Trans>,
            cell: ({ item }) => <CellLastEdit item={item} />,
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("medicaldata", item.uuid, editedField);
          await refetch();
        }}
        onDelete={async (item) => {
          await patient.deleteItem("medicaldata", item.uuid);
          await refetch();
        }}
      />
    </PatientSubpage>
  );
});
